/**
 * The pieces every panel screen assembles from: the action-button row along
 * the bottom, prose wrapped to a measured column, and the original's boxed
 * template slots.
 *
 * The captures put the action row at y=176 (`patrol-briefing.png`,
 * `career-enlist-name.png`), 178 (`mission-assignment.png`), 179
 * (`flight-assignments.png`) and 180 (the two list screens), and size each
 * button to its own label — but not by any rule that fits all of them
 * (`RETURN TO MAIN` is 98px wide on one screen and 101 on another). So the
 * row here is the original's *shape* with the port's own arithmetic: the
 * padding is as generous as the frame allows, the row is centred, and the
 * gutter is 8.
 *
 * What the arithmetic is not free to do is print a label wider than the plate
 * it sits on — see `ACTION_PADDING_MIN`, which is measured off the same
 * captures, and `actionRow`, which refuses a row that cannot be built at it.
 */

import { measureText, wrapText, type FontAtlas } from "../render/text-metrics.ts";
import { BUTTON_FONT, BUTTON_PLATE_INSET, BUTTON_PRESS_NUDGE } from "../render/board-chrome.ts";
import { LOGICAL_WIDTH, type Rect } from "../render/draw-ops.ts";
import { slotRect, slotWidth } from "../render/plate-chrome.ts";
import type { MenuAction } from "./menu-model.ts";
import type { ListRow, ShellScreen, Widget } from "./shell-screen.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

export const ACTION_ROW_Y = 178;
export const ACTION_HEIGHT = 18;
export const ACTION_MARGIN = 13;
/**
 * How narrow the original lets the side margin get when a row is tight:
 * `flight-assignments.png` sets its three buttons at x=10 and ends at x=310,
 * against `patrol-briefing.png`'s roomier 16/16. `ACTION_MARGIN` is what the
 * row *asks* for; this is the point past which it has stopped being a margin.
 */
export const ACTION_MARGIN_MIN = 9;
export const ACTION_GUTTER = 8;
const ACTION_PADDING_MAX = 20;
/**
 * A button's label has to fit the button's *plate*, not its rectangle.
 *
 * `boardButtonOps` sinks the orange plate five pixels inside the rect on every
 * side, and the captures agree exactly: on all nine action-row buttons of
 * `mission-assignment.png`, `patrol-briefing.png` and `flight-assignments.png`
 * the plate runs `rect.x + 5 .. rect.x + w - 6`, with no exception. So a label
 * only lands on its own plate when the button is at least **ten** pixels wider
 * than the label's ink.
 *
 * The original's own tightest is 12: rect width minus the label's FONT1
 * advance, across those same nine, comes to 12 / 16 / 19 / 18 / 21 / 27 / 32 /
 * 32 / 37 — one column of plate showing each side at worst, and
 * `ACTION_PADDING_MAX` of 20 already sits inside that band.
 *
 * The floor is 14 rather than 12 because of something the original does not
 * have: `boardButtonOps` draws the *selected* button pressed, shifting its
 * label a pixel down and right, since `PS.EXE` lights a button only while the
 * mouse is held on it and the port needs a keyboard cursor. Twelve leaves a
 * clamped label flush with the plate's right edge, and the nudge then puts it
 * on the bevel. Two pixels of the port's own budget pay for the port's own
 * cursor.
 *
 * It used to be 6. Every row that clamped to it drew its labels four pixels
 * wider than the plate they sit on, and on `MISSION ASSIGNMENT` — which was
 * also measuring in the wrong atlas — two of the four ran outside the button
 * rectangle altogether.
 */
const ACTION_PADDING_MIN = 14;

/** The floor is a choice, but it may never drop below what the chrome physically needs. */
const PLATE_FLOOR = 2 * BUTTON_PLATE_INSET + 2 * BUTTON_PRESS_NUDGE;
if (ACTION_PADDING_MIN < PLATE_FLOOR) throw new Error("shell: the action row's padding floor is under the button plate's own inset");

export interface ActionSpec {
  readonly id: string;
  readonly label: string;
  readonly action: MenuAction;
  readonly enabled?: boolean;
}

/**
 * How much wider than its label a button is. The captures' own padding runs
 * from 12 to 37 with no rule behind it, so the row takes as much as it can up
 * to 20 and shrinks — but only as far as `ACTION_PADDING_MIN`, below which the
 * label would no longer be on its own plate.
 */
function actionPadding(labelWidths: readonly number[]): number {
  const count = labelWidths.length;
  if (count === 0) return ACTION_PADDING_MAX;
  const available = LOGICAL_WIDTH - 2 * ACTION_MARGIN - ACTION_GUTTER * (count - 1);
  const slack = available - labelWidths.reduce((sum, width) => sum + width, 0);
  return Math.max(ACTION_PADDING_MIN, Math.min(ACTION_PADDING_MAX, Math.floor(slack / count)));
}

/** The frame a row may occupy once it has given up the margin it would prefer. */
const ACTION_ROW_LIMIT = LOGICAL_WIDTH - 2 * ACTION_MARGIN_MIN;

/**
 * The action row's buttons, sized to their labels **in the atlas the button
 * will draw them in**.
 *
 * That is `boardButtonOps`'s `BUTTON_FONT`, i.e. FONT1 — a caller that hands
 * this its FONT3 body atlas gets a row measured 4 to 13 pixels short per label
 * and the labels spill over the chrome, which is exactly what `MISSION
 * ASSIGNMENT` did.
 *
 * A row whose labels cannot fit the frame even at `ACTION_PADDING_MIN` is a
 * screen-authoring error rather than something to draw crammed, so it throws:
 * there is no arrangement of that many labels at that length which the button
 * chrome can hold, and the original never asks for one. It is how a fourth
 * button on `MISSION ASSIGNMENT` announces itself.
 */
export function actionRow(actions: readonly ActionSpec[], font: FontAtlas, y = ACTION_ROW_Y): Widget[] {
  // Checked rather than documented: `FontAtlas` carries its own id, both atlases
  // satisfy the type, and the wrong one is the right one for the rest of the
  // screen — which is exactly how `MISSION ASSIGNMENT` came to size its buttons
  // 4 to 13 pixels short per label without anything complaining.
  if (font.id !== BUTTON_FONT) {
    throw new Error(`shell: an action row must be measured in ${BUTTON_FONT}, which is what its buttons draw in — got ${font.id}`);
  }
  const labels = actions.map((entry) => measureText(font, entry.label));
  const padding = actionPadding(labels);
  const widths = labels.map((width) => width + padding);
  const total = widths.reduce((sum, w) => sum + w, 0) + ACTION_GUTTER * (actions.length - 1);
  if (total > ACTION_ROW_LIMIT) {
    const named = actions.map((entry) => entry.label).join(" / ");
    throw new Error(
      `shell: the action row ${named} needs ${total}px at the tightest padding its buttons can hold, and the frame has ${ACTION_ROW_LIMIT}`,
    );
  }
  let x = Math.floor((LOGICAL_WIDTH - total) / 2);
  return actions.map((entry, index) => {
    const rect: Rect = { x, y, w: widths[index] ?? 0, h: ACTION_HEIGHT };
    x += (widths[index] ?? 0) + ACTION_GUTTER;
    return { kind: "button", id: entry.id, rect, label: entry.label, enabled: entry.enabled !== false, action: entry.action };
  });
}

/** Re-exported so a screen already importing its panel pieces from here does not need a second import for the wrap. */
export { wrapText };

/**
 * One value `boxValues` can find in a sentence. `action` makes the resulting
 * `slot` widget clickable (AD-011's "Clicking on one will bring up a menu
 * with your possible options") — omit it for a decorative box, the only kind
 * before this existed.
 */
export interface BoxedValue {
  readonly value: string;
  readonly action?: MenuAction;
}

/**
 * A line of prose with some of its values boxed, the way every briefing screen
 * draws its template slots. Segments alternate plain and boxed; the boxed ones
 * become `slot` widgets and the plain ones ordinary text, all on one baseline.
 */
export interface Segment {
  readonly text: string;
  readonly boxed: boolean;
  readonly action?: MenuAction;
}

export interface SegmentLayout {
  readonly x: number;
  readonly y: number;
  readonly maxWidth: number;
  readonly pitch: number;
  readonly maxLines: number;
}

/** One piece of a segmented sentence, already assigned a column and a row. */
interface Placement {
  readonly text: string;
  readonly boxed: boolean;
  readonly action: MenuAction | undefined;
  readonly x: number;
  readonly row: number;
  readonly width: number;
}

/**
 * Wraps a segmented sentence into rows, stopping at `maxLines`. A boxed segment
 * never splits — the original's `[boxed]` slots are single clickable values, so
 * they move whole.
 *
 * **The `maxLines` check has to run after each wrap, boxed segments included**,
 * not once per segment at the top of the loop: a boxed value that wraps onto the
 * row past the last allowed one is a slot drawn *below the block* — on the
 * briefing plate, at y=175, eight pixels inside the action row at 176, where it
 * shadows the button's own hit rect, because `focusAt` takes the first match and
 * slots are declared before buttons. A widget outside its own box is not merely
 * ugly here; it steals the click.
 */
function placeSegments(segments: readonly Segment[], x: number, maxWidth: number, maxLines: number, font: FontAtlas): Placement[] {
  const placed: Placement[] = [];
  let pen = x;
  let row = 0;
  const wrap = (width: number): void => {
    if (pen + width <= x + maxWidth || pen === x) return;
    row += 1;
    pen = x;
  };
  for (const segment of segments) {
    if (row >= maxLines) break;
    const pieces = segment.boxed ? [segment.text] : splitKeepingSpaces(segment.text);
    for (const piece of pieces) {
      const width = segment.boxed ? slotWidth(font, piece) : measureText(font, piece);
      wrap(width);
      if (row >= maxLines) break;
      placed.push({ text: piece, boxed: segment.boxed, action: segment.action, x: pen, row, width });
      pen += segment.boxed ? width + 2 : width;
    }
  }
  return placed;
}

/**
 * How many rows a segmented sentence needs at this column width — the number a
 * caller sizing its own block has to know *before* it can choose where the block
 * starts and how tightly it is set.
 */
export function segmentRows(segments: readonly Segment[], maxWidth: number, font: FontAtlas): number {
  const placed = placeSegments(segments, 0, maxWidth, Number.MAX_SAFE_INTEGER, font);
  return (placed.at(-1)?.row ?? -1) + 1;
}

/** Lays a segmented sentence out as widgets, wrapping onto the next line when a segment would run past the plate. */
export function segmentWidgets(segments: readonly Segment[], layout: SegmentLayout, font: FontAtlas): Widget[] {
  return placeSegments(segments, layout.x, layout.maxWidth, layout.maxLines, font).map((piece) => {
    const y = layout.y + piece.row * layout.pitch;
    return piece.boxed
      ? { kind: "slot", rect: slotRect(piece.x, y, piece.width), text: piece.text, action: piece.action }
      : { kind: "text", text: piece.text, x: piece.x, y, role: "prose" };
  });
}

/** Splits on spaces but keeps them attached, so a wrapped line does not lose its word gaps. */
function splitKeepingSpaces(text: string): string[] {
  return text.split(/(?<=\s)/).filter((part) => part !== "");
}

const WORD_CHAR = /[A-Za-z0-9]/;

/**
 * The first *whole-word* occurrence of `value` in `text` — plain `indexOf`
 * would just as happily match "light" inside "f**light**", which is exactly
 * what AD-011 hit the first time a drawn wind of "light" landed in "Your
 * flight of ..." once `enemyAircraft`/`region` overrides started shifting
 * which weather a seed draws. A hit only counts when neither neighbour is a
 * letter or digit, so a value can still open on a hyphen, a comma or a
 * sentence's own start/end.
 */
function wholeWordIndexOf(text: string, value: string): number {
  if (value === "") return -1;
  let from = 0;
  for (;;) {
    const at = text.indexOf(value, from);
    if (at < 0) return -1;
    const before = text[at - 1];
    const after = text[at + value.length];
    if (!(before && WORD_CHAR.test(before)) && !(after && WORD_CHAR.test(after))) return at;
    from = at + 1;
  }
}

/**
 * Splits a rendered sentence into plain and boxed segments by finding each
 * value verbatim inside it. A bare string is shorthand for a `BoxedValue`
 * with no `action` — every existing caller's decorative boxes still work
 * unchanged; only a value passed as `{ value, action }` becomes clickable.
 */
export function boxValues(sentence: string, values: readonly (string | BoxedValue)[]): Segment[] {
  const entries: BoxedValue[] = values.map((entry) => (typeof entry === "string" ? { value: entry } : entry));
  const segments: Segment[] = [];
  let rest = sentence;
  while (rest.length > 0) {
    const hit = entries
      .map((entry) => ({ entry, at: wholeWordIndexOf(rest, entry.value) }))
      .filter((candidate) => candidate.at >= 0)
      .sort((a, b) => a.at - b.at || b.entry.value.length - a.entry.value.length)[0];
    if (!hit) {
      segments.push({ text: rest, boxed: false });
      break;
    }
    if (hit.at > 0) segments.push({ text: rest.slice(0, hit.at), boxed: false });
    segments.push({ text: hit.entry.value, boxed: true, action: hit.entry.action });
    rest = rest.slice(hit.at + hit.entry.value.length);
  }
  return segments;
}

/**
 * The plate + prompt + list-box shape every roster/leaderboard screen shares —
 * `CONTINUE A CAREER`'s pilot picker, `VIEW TOP ACES`, and `BEST MISSIONS`
 * all draw this same well, differing only in title, prompt, rows and the
 * button row underneath. Lifted out of `career-screens.ts` once a second
 * caller (`missions-screens.ts`) needed the identical widget list.
 */
export const LIST_RECT: Rect = { x: 28, y: 39, w: 156, h: 61 };

export interface ListScreenSpec {
  readonly id: string;
  readonly title: string;
  /** `SELECT HISTORIC MISSION:` and its siblings — FONT3 bold above the well. */
  readonly prompt: string;
  readonly rows: readonly ListRow[];
  readonly actions: readonly ActionSpec[];
}

/** The list widget's scroll state — a subset of whichever surface model owns it, so any caller's model shape satisfies this without adapting. */
export interface ListCursor {
  readonly listIndex: number;
  readonly listTop: number;
}

/** The atlas is the *button* atlas (FONT1): the prompt and the rows are drawn by the renderer in their own role fonts, so the only thing measured here is the action row. */
export function listScreen(spec: ListScreenSpec, cursor: ListCursor, heading: FontAtlas): ShellScreen {
  return {
    id: spec.id,
    chrome: { kind: "panel", title: spec.title, emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: { x: 16, y: 24, w: 286, h: 96 } },
      { kind: "text", text: spec.prompt, x: 27, y: 28, role: "prompt" },
      {
        kind: "list",
        id: "rows",
        rect: LIST_RECT,
        rows: spec.rows,
        selected: cursor.listIndex,
        top: cursor.listTop,
        action: command("select"),
      },
      ...actionRow(spec.actions, heading, 180),
    ],
    cancel: spec.actions[0]?.action,
  };
}
